import { useRef, type ReactNode } from 'react';
import { LAYOUT_LIMITS } from '@jerome2/shared';
import { useHeight, useWidth } from '../lib/size.ts';
import { fitSplit, fitWidth, percentText, pixelsText, splitBounds, useLayout, widthBounds } from '../state/layout.ts';
import { Resizer } from '../ui/Resizer.tsx';
import { MailListStrip } from './MailListStrip.tsx';

// De indeling van lijst en lezer op het tabblad Mail (ticket 28625); MailScreen geeft de inhoud en de keuzes (readerLayout.ts).
// Op de telefoon: de lijst óf de lezer (met een open mail). Op de desktop staat de lezer rechts van de lijst (deel 1) of eronder,
// zoals in Outlook (deel 2, `below`). Rechts is de lijst zo breed als `layout.mailList`, met een staande greep ertussen, en houdt
// de lezer 480 px van de gemeten rij. Onder ligt de lijst over de volle breedte boven de lezer, zo hoog als `layout.mailSplit` van
// de gemeten kolom, met een liggende greep ertussen; de lezer houdt 240 px en de lijst 120 px (state/layout.ts). Past de instelling
// niet, dan wijkt de lijst zonder dat de instelling verandert. Met de lijst ingeklapt (`collapsed`) staat de strook in plaats van
// lijst en greep: staand links van de lezer, of liggend erboven. De rij of kolom is één element, zodat de metingen doorlopen als
// de plek wisselt.

/** Zolang er niets te meten valt (de eerste weergave, en tests zonder ResizeObserver): de breedte van de rij en de hoogte van de
 * kolom van lijst en lezer. */
const ROW_FALLBACK = 1000;
const COLUMN_FALLBACK = 700;

export interface MailLayoutProps {
  narrow: boolean;
  /** Een mail open: op de telefoon staat dan de lezer in plaats van de lijst. */
  mailOpen: boolean;
  /** Desktop: de lezer onder de lijst in plaats van ernaast. */
  below: boolean;
  /** Desktop: de lijst ingeklapt tot de strook (de keuze uit, met een open mail). */
  collapsed: boolean;
  /** Het aantal ongelezen mails volgens de server (alle ongelezen mail, zoals Ongelezen in de filterbalk), voor de strook; null zolang dat niet bekend is. */
  unread: number | null;
  /** De knop op de strook: de lijst weer tonen. */
  onShowList: () => void;
  /** De lijst (met Klaar voor jou erboven) en de lezer (met de instructiebalk eronder, de editor en de bijlageviewer erover). */
  list: ReactNode;
  reader: ReactNode;
}

export function MailLayout(props: MailLayoutProps) {
  const { narrow, below, collapsed } = props;
  const box = useRef<HTMLDivElement>(null);
  const width = useWidth(box, narrow ? 0 : ROW_FALLBACK);
  const height = useHeight(box, narrow ? 0 : COLUMN_FALLBACK);
  const listLayout = useLayout('mailList');
  const split = useLayout('mailSplit');

  // De lezer neemt de rest: naast de lijst de breedte, onder de lijst de hoogte. Relatief: de editor en de bijlageviewer liggen erover.
  const reader = <div className={below && !narrow ? 'flex-1 min-h-0 min-w-0 flex flex-col relative' : 'flex-1 min-w-0 h-full flex flex-col relative'}>{props.reader}</div>;

  if (narrow) {
    return (
      <div ref={box} className="flex-1 min-h-0 flex">
        {props.mailOpen ? reader : <div className="w-full h-full min-h-0 flex flex-col">{props.list}</div>}
      </div>
    );
  }

  const strip = <MailListStrip unread={props.unread} onExpand={props.onShowList} orientation={below ? 'horizontal' : 'vertical'} />;
  let list: ReactNode = null;
  let grip: ReactNode = null;
  if (!collapsed && below) {
    const bounds = splitBounds('mailSplit', height);
    const share = fitSplit('mailSplit', split.value, height);
    list = (
      <div className="shrink-0 border-b border-line min-h-0 flex flex-col" style={{ height: Math.round(height * share) }}>
        {props.list}
      </div>
    );
    grip = <Resizer axis="y" label="Hoogte maillijst" value={share * 100} min={bounds.min * 100} max={bounds.max * 100} defaultValue={LAYOUT_LIMITS.mailSplit.default * 100} side="left" unit={height / 100} valueText={percentText('lijst')} onChange={(v) => split.change(v / 100)} onCommit={(v) => split.commit(v / 100)} />;
  } else if (!collapsed) {
    // Rechts: zo breed als ingesteld (300 tot 640 px, standaard 380; onder 300 px wikkelen de rijen), en nooit zo breed dat de
    // lezer minder dan 480 px van de rij houdt.
    const bounds = widthBounds('mailList', width);
    list = (
      <div className="shrink-0 border-r border-line h-full min-h-0 flex flex-col" style={{ width: fitWidth('mailList', listLayout.value, width) }}>
        {props.list}
      </div>
    );
    grip = <Resizer label="Breedte maillijst" value={fitWidth('mailList', listLayout.value, width)} min={bounds.min} max={bounds.max} defaultValue={LAYOUT_LIMITS.mailList.default} side="left" valueText={pixelsText} onChange={listLayout.change} onCommit={listLayout.commit} />;
  }

  return (
    <div ref={box} className={`flex-1 min-h-0 flex ${below ? 'flex-col' : ''}`}>
      {collapsed ? strip : list}
      {grip}
      {reader}
    </div>
  );
}
